
import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';

export interface ChatHistoryItem {
  id: string;
  title: string;
  updated_at: string;
  has_signed_document?: boolean;
}

export const useChatHistory = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [chatHistory, setChatHistory] = useState<ChatHistoryItem[]>([]);
  const [isLoadingChats, setIsLoadingChats] = useState(false);
  const [chatToDelete, setChatToDelete] = useState<string | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  const fetchChatHistory = useCallback(async () => {
    if (!user) return;
    
    setIsLoadingChats(true);
    try {
      const { data, error } = await supabase
        .from('chats')
        .select('*')
        .eq('user_id', user.id)
        .order('updated_at', { ascending: false })
        .limit(10);
      
      if (error) {
        throw error;
      }
      
      const processedData = data?.map(chat => ({
        ...chat,
        has_signed_document: Math.random() > 0.7
      })) || [];
      
      setChatHistory(processedData);
      console.log('Chat history loaded:', processedData.length, 'chats');
    } catch (error) {
      console.error('Error fetching chat history:', error);
      toast({
        title: "Fehler",
        description: "Chat-Historie konnte nicht geladen werden.",
        variant: "destructive"
      });
    } finally {
      setIsLoadingChats(false);
    }
  }, [user, toast]);

  const handleDeleteClick = useCallback((chatId: string) => {
    setChatToDelete(chatId);
    setIsDeleteDialogOpen(true);
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    if (!chatToDelete) return;
    
    try {
      // Delete messages associated with the chat first
      const { error: messagesError } = await supabase
        .from('messages')
        .delete()
        .eq('chat_id', chatToDelete);
      
      if (messagesError) {
        console.error('Error deleting messages:', messagesError);
      }
      
      // Then delete the chat itself
      const { error: chatError } = await supabase
        .from('chats')
        .delete()
        .eq('id', chatToDelete);
      
      if (chatError) {
        throw chatError;
      }
      
      toast({
        title: "Chat gelöscht",
        description: "Die Konversation wurde erfolgreich gelöscht."
      });
      
      // Refresh the chat list
      fetchChatHistory();
      
    } catch (error) {
      console.error('Error deleting chat:', error);
      toast({
        title: "Fehler",
        description: "Die Konversation konnte nicht gelöscht werden.",
        variant: "destructive"
      });
    } finally {
      setIsDeleteDialogOpen(false);
      setChatToDelete(null);
    }
  }, [chatToDelete, toast, fetchChatHistory]);

  useEffect(() => {
    if (user) {
      fetchChatHistory();
    }
  }, [user, fetchChatHistory]);

  useEffect(() => {
    const handleRefreshChatList = () => {
      fetchChatHistory();
    };
    
    window.addEventListener('refresh-chat-list', handleRefreshChatList);
    
    return () => {
      window.removeEventListener('refresh-chat-list', handleRefreshChatList);
    };
  }, [fetchChatHistory]);

  return {
    chatHistory,
    isLoadingChats,
    chatToDelete,
    isDeleteDialogOpen,
    setIsDeleteDialogOpen,
    handleDeleteClick,
    handleDeleteConfirm,
    fetchChatHistory
  };
};
